import { z } from 'zod';

/**
 * Media-signal SURGE CLUSTER — the surge-detector card contract (#349). One row
 * = one OPEN igniter cluster (an active/confirmed `media_signal_anomalies` row),
 * exposed as a CHEAP COUNT CARD that needs NO LLM brief: it leads the News-tab
 * social-surge lane with the count signal ("N posts about <hazard> near <place>
 * · M outlets · trending · not confirmed") instead of a per-post firehose.
 *
 * Sourced straight from the persisted igniter cluster (count / origins / geo /
 * hazard), with an OPTIONAL `bulletinId` LEFT-JOINed from `media_signal_bulletins`
 * by cluster_key — the LLM "what's happening" brief that sits behind the card's
 * EXPAND when one was generated. SUPPRESSED clusters (officials already
 * reporting) have no anomaly row, so they never become surge cards; their
 * corroboration bulletins still render as standalone bulletin cards.
 *
 * HONESTY: a surge card is `trending · not confirmed` — it is NEVER auto-urgent
 * and NEVER scores a Response Level. The igniter governs the Alerts queue; this
 * contract is display-only.
 */
export const MediaSignalClusterSchema = z.object({
  /** The igniter's stable cluster identity (media:<hazard>:<latCell>:<lonCell>) —
   *  the key the surge lane groups its underlying posts under. */
  clusterKey: z.string(),
  hazardKey: z.string(),
  /** Distinct stories in the cluster (the N in "N posts"). */
  count: z.number().int().nonnegative(),
  /** Distinct upstream origins / outlets (the M in "M outlets"). */
  origins: z.number().int().nonnegative(),
  /** Cluster centroid — honest geo (centroid of source-provided points). */
  geo: z.object({
    lon: z.number().min(-180).max(180),
    lat: z.number().min(-90).max(90),
  }),
  radiusM: z.number(),
  /** Representative place label (a member signal's geo_name), or null. Drives the
   *  "near <place>" card copy; the card degrades gracefully when absent. */
  place: z.string().nullable(),
  /** When the cluster was first detected (the surge's "first seen"). */
  firstSignalAt: z.string(),
  /** The most-recent surge brief for this cluster, when one exists — the optional
   *  brief-on-expand link; null when no bulletin was generated. */
  bulletinId: z.string().uuid().nullable(),
});
export type MediaSignalCluster = z.infer<typeof MediaSignalClusterSchema>;

export const MediaSignalClusterListResponseSchema = z.object({
  clusters: z.array(MediaSignalClusterSchema),
});
export type MediaSignalClusterListResponse = z.infer<
  typeof MediaSignalClusterListResponseSchema
>;
